import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, FileSignature, FileText, Sparkles } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { ErrorState } from '../../components/ui/EmptyState';
import { useConfirm } from '../../components/ui/confirm';
import { useConsequence } from '../../components/ui/ConsequencePreview';
import { useToast } from '../../components/ui/toast';
import { todayISO } from '../../lib/thai-date';
import {
  useContractTemplates, useDocumentsOverview, useDraftContractClauses, useGenerateContract, type ContractTemplateDoc,
} from '../../hooks';
import { apiFetch } from '../../lib/api-client';

const NOTE_MAX = 4000;
import { openWhenReady } from '../../lib/open-when-ready';

function readApiError(error: unknown): { code: string; message: string } {
  if (error instanceof Error) {
    return { code: String((error as Error & { code?: string }).code ?? ''), message: error.message };
  }
  return { code: '', message: 'ให้ AI เสนอเงื่อนไขไม่สำเร็จ' };
}

export function ContractDraft() {
  const { empCode: routeEmpCode } = useParams<{ empCode: string }>();
  const empCode = routeEmpCode?.trim() ?? '';
  const { data: overview, isLoading, isError, error, refetch } = useDocumentsOverview();
  const employee = overview?.employees.find((item) => item.emp_code === empCode);
  const generate = useGenerateContract();
  const draftClauses = useDraftContractClauses();
  const confirm = useConfirm();
  const consequence = useConsequence();
  const toast = useToast();
  const [effectiveDate, setEffectiveDate] = useState(todayISO());
  const [probationMonths, setProbationMonths] = useState(0);
  const [note, setNote] = useState('');
  const noteRef = useRef(note);
  noteRef.current = note; // ข้อความล่าสุดเสมอ — ใช้ตอนโหลดข้อตกลงพิเศษเสร็จ (พิมพ์ระหว่างรอต้องไม่หาย)
  const [endDate, setEndDate] = useState('');
  // คลังเอกสารสัญญา — ค่าเริ่มต้น = ฉบับติดดาว · โหลดไม่ได้ (ยังไม่รัน migration) = ซ่อนช่อง ระบบใช้ถ้อยคำเดิม
  const templatesQuery = useContractTemplates();
  const contractTemplates = useMemo(
    () => (templatesQuery.data?.templates ?? []).filter((t) => t.category === 'contract' && t.is_active).sort((a, b) => a.sort - b.sort),
    [templatesQuery.data],
  );
  const addenda = useMemo(
    () => (templatesQuery.data?.templates ?? []).filter((t) => t.category === 'addendum' && t.is_active).sort((a, b) => a.sort - b.sort),
    [templatesQuery.data],
  );
  const [templateId, setTemplateId] = useState('');
  useEffect(() => {
    if (templateId && contractTemplates.some((t) => t.id === templateId)) return;
    setTemplateId((contractTemplates.find((t) => t.is_default) ?? contractTemplates[0])?.id ?? '');
  }, [contractTemplates, templateId]);
  const selectedTemplate = contractTemplates.find((t) => t.id === templateId);
  const [addingAddendum, setAddingAddendum] = useState(false);

  async function addAddendum(id: string) {
    if (!id) return;
    setAddingAddendum(true);
    try {
      const { template } = await apiFetch<{ template: ContractTemplateDoc }>(`/hr/contract-templates/${id}`);
      const block = `${template.name}\n${(template.body_text ?? '').trim()}`;
      const current = noteRef.current.trim();
      const next = current ? `${current}\n\n${block}` : block;
      if (next.length > NOTE_MAX) {
        toast.error(`ใส่ไม่ได้ — เงื่อนไขเพิ่มเติมจะยาวเกิน ${NOTE_MAX.toLocaleString('th-TH')} ตัวอักษร (ตัดข้อความเดิมก่อน)`);
        return;
      }
      setNote(next);
      toast.success(`ใส่ "${template.name}" ในเงื่อนไขเพิ่มเติมแล้ว — ตรวจแก้ก่อนสร้างสัญญา`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'โหลดข้อตกลงพิเศษไม่สำเร็จ');
    } finally {
      setAddingAddendum(false);
    }
  }
  const [aiError, setAiError] = useState<{ code: string; message: string } | null>(null);

  useEffect(() => {
    if (employee?.start_date) setEffectiveDate(employee.start_date.slice(0, 10));
  }, [employee?.start_date]);

  async function suggestClauses() {
    if (note.trim()) {
      const ok = await confirm({
        title: 'แทนที่เงื่อนไขเพิ่มเติมเดิม?',
        message: 'AI จะเสนอข้อความใหม่แทนข้อความในช่องนี้ แต่ยังไม่สร้างหรือส่งสัญญา',
        confirmText: 'เสนอใหม่',
      });
      if (!ok) return;
    }
    setAiError(null);
    try {
      const result = await draftClauses.mutateAsync({ emp_code: empCode });
      setNote(result.note);
      toast.success('เพิ่มข้อเสนอจาก AI แล้ว ตรวจแก้ก่อนสร้างสัญญา');
    } catch (err) {
      setAiError(readApiError(err));
    }
  }

  async function createContract() {
    if (!effectiveDate) {
      toast.error('กรอกวันเริ่มสัญญา');
      return;
    }
    if (endDate && endDate < effectiveDate) {
      toast.error('วันสิ้นสุดสัญญาต้องไม่ก่อนวันเริ่มสัญญา');
      return;
    }
    const ok = await consequence({
      title: 'สร้างสัญญาจ้างงาน',
      intro: `ระบบจะสร้างสัญญาจ้าง PDF ให้ ${employee?.name ?? empCode} และแจ้งเตือนให้เข้าไปเซ็นทาง LINE`,
      lines: [
        { label: 'วันเริ่มสัญญา', value: new Date(`${effectiveDate}T00:00:00`).toLocaleDateString('th-TH'), emphasis: true },
        { label: 'ระยะทดลองงาน', value: probationMonths > 0 ? `${probationMonths} เดือน` : 'ไม่ระบุ' },
        ...(endDate ? [{ label: 'วันสิ้นสุดสัญญา', value: new Date(`${endDate}T00:00:00`).toLocaleDateString('th-TH') }] : []),
        ...(selectedTemplate ? [{ label: 'เอกสารสัญญา', value: selectedTemplate.name }] : []),
      ],
      effects: ['สร้างไฟล์ PDF สัญญาจ้างงาน', 'แจ้งพนักงานทาง LINE ให้เข้าไปเซ็นเอกสาร'],
      confirmText: 'สร้างสัญญา',
    });
    if (!ok) return;
    try {
      await openWhenReady(async () => {
        const result = await generate.mutateAsync({
          emp_code: empCode,
          effective_date: effectiveDate,
          probation_months: probationMonths,
          note: note.trim(),
          ...(templateId ? { template_id: templateId } : {}),
          ...(endDate ? { end_date: endDate } : {}),
        });
        return result.pdf_url;
      });
      toast.success('สร้างสัญญาจ้างสำเร็จ');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'สร้างสัญญาไม่สำเร็จ');
    }
  }

  return (
    <AdminLayout title="ร่างสัญญาจ้าง">
      <div className="mx-auto max-w-2xl space-y-4 pb-12">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Link to="/owner/documents" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
            <ArrowLeft className="h-4 w-4" /> กลับไปเอกสารพนักงาน
          </Link>
          <Link to="/owner/contract-template" className="btn-secondary btn-slim inline-flex items-center gap-1.5">
            <FileText className="h-4 w-4" /> แก้ถ้อยคำสัญญา
          </Link>
        </div>

        {isLoading && <div className="card p-8 text-center text-sm text-gray-500">กำลังโหลด…</div>}
        {isError && <div className="card"><ErrorState detail={error instanceof Error ? error.message : undefined} onRetry={() => void refetch()} /></div>}
        {!isLoading && !isError && !employee && <div className="card"><ErrorState title="ไม่พบพนักงาน" detail="กลับไปเลือกรายการพนักงานจากหน้าเอกสารพนักงาน" /></div>}

        {employee && (
          <>
            <div className="card p-5">
              <p className="text-xs text-gray-500">พนักงาน</p>
              <h2 className="mt-1 text-lg font-semibold text-gray-900">{employee.name}</h2>
              <p className="mt-1 text-sm text-gray-500">รหัส {employee.emp_code}{employee.department ? ` · ${employee.department}` : ''}</p>
            </div>

            <div className="card space-y-4 p-5">
              {contractTemplates.length > 0 && (
                <div>
                  <label className="label">ใช้เอกสารสัญญา</label>
                  <select className="input w-full" value={templateId} onChange={(event) => setTemplateId(event.target.value)}>
                    {contractTemplates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (ค่าเริ่มต้น)' : ''}</option>
                    ))}
                  </select>
                  {templateId && (
                    <Link to={`/owner/contract-template/${templateId}`} className="mt-1 inline-block text-xs text-brand-700 hover:underline">
                      เปิดดู/แก้เอกสารนี้
                    </Link>
                  )}
                </div>
              )}
              <div>
                <label className="label">วันเริ่มสัญญา</label>
                <input type="date" className="input w-full" value={effectiveDate} onChange={(event) => setEffectiveDate(event.target.value)} />
              </div>
              <div>
                <label className="label">ระยะทดลองงาน (เดือน)</label>
                <input
                  type="number"
                  min={0}
                  max={4}
                  step={1}
                  className="input w-full"
                  value={probationMonths}
                  // ตัดทศนิยม — schema รับจำนวนเต็มเท่านั้น (พิมพ์ 1.5 เดิมผ่านจอแต่หลังบ้านปฏิเสธตอนสร้างสัญญา)
                  onChange={(event) => setProbationMonths(Math.min(4, Math.max(0, Math.trunc(Number(event.target.value) || 0))))}
                />
                <p className="mt-1 text-xs text-gray-500">เลือก 0 หากไม่ต้องการระบุระยะทดลองงาน</p>
              </div>
              <div>
                <label className="label">วันสิ้นสุดสัญญา (เฉพาะสัญญามีกำหนดเวลา)</label>
                <input type="date" className="input w-full" value={endDate} min={effectiveDate || undefined} onChange={(event) => setEndDate(event.target.value)} />
                <p className="mt-1 text-xs text-gray-500">เว้นว่าง = ไม่มีกำหนด</p>
              </div>
              <div>
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <label className="label mb-0">เงื่อนไขเพิ่มเติม</label>
                  <button type="button" onClick={() => void suggestClauses()} disabled={draftClauses.isPending} className="btn-secondary btn-slim inline-flex items-center gap-1.5">
                    <Sparkles className="h-4 w-4" /> {draftClauses.isPending ? 'กำลังเสนอ…' : 'ให้ AI เสนอเงื่อนไขตามตำแหน่ง'}
                  </button>
                </div>
                {addenda.length > 0 && (
                  <select
                    className="input mb-2 w-full"
                    value=""
                    disabled={addingAddendum}
                    onChange={(event) => void addAddendum(event.target.value)}
                    aria-label="ใส่ข้อตกลงพิเศษจากคลังเอกสาร"
                  >
                    <option value="">{addingAddendum ? 'กำลังใส่…' : '+ ใส่ข้อตกลงพิเศษจากคลังเอกสาร (ห้ามค้าแข่ง รักษาความลับ ฯลฯ)'}</option>
                    {addenda.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                )}
                <textarea
                  className="input min-h-[180px] w-full"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  maxLength={NOTE_MAX}
                  placeholder="เงื่อนไขเฉพาะตำแหน่งหรือข้อตกลงเพิ่มเติม"
                />
                <p className="mt-1 text-right text-xs text-gray-400">{note.length.toLocaleString('th-TH')}/{NOTE_MAX.toLocaleString('th-TH')}</p>
              </div>
              <p className="text-xs text-gray-500">AI เสนอเฉพาะฉบับร่าง ยังไม่สร้างไฟล์ ไม่บันทึก และไม่ส่งให้พนักงาน</p>
              {aiError && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  {aiError.message}
                  {aiError.code === 'AI_NOT_CONFIGURED' && (
                    <Link to="/owner/settings#ai" className="ml-1 font-medium underline">ไปที่ตั้งค่า AI</Link>
                  )}
                </div>
              )}
            </div>

            <button type="button" onClick={() => void createContract()} disabled={generate.isPending || addingAddendum} className="btn-cta w-full justify-center">
              <FileSignature className="h-4 w-4" /> {generate.isPending ? 'กำลังสร้าง…' : 'ตรวจสอบและสร้างสัญญา'}
            </button>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
